import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Drawer,
  IconButton,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CloseIcon,
  ExpandMoreIcon,
  UIButton
} from '@/ui';
import { TransitionProps } from '@mui/material/transitions';
import type { FaqContent } from '@/shared/help/faqTypes';

export interface FAQPanelProps {
  open: boolean;
  onClose: () => void;
  content: FaqContent;
}

const SlideUpTransition = React.forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
  function Transition(props, ref) {
    // Type assertion needed due to exactOptionalPropertyTypes strict mode
    return <Slide direction="up" ref={ref} {...(props as any)} />;
  }
);

export default function FAQPanel({ open, onClose, content }: FAQPanelProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedIndex, setExpandedIndex] = useState<number | false>(false);

  const handleToggle = (index: number) => (_: unknown, isExpanded: boolean) => {
    setExpandedIndex(isExpanded ? index : false);
  };

  const body = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: 2, md: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        minHeight: '64px'
      }}>
        <Stack spacing={0.25}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            Frequently Asked Questions
          </Typography>
          {content.updatedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Updated {content.updatedAt}
            </Typography>
          )}
        </Stack>
        <IconButton onClick={onClose} sx={{ p: 1, backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
        <Stack spacing={1.5}>
          {content.items.map((item, idx) => (
            <Accordion 
              key={idx} 
              expanded={expandedIndex === idx} 
              onChange={handleToggle(idx)} 
              sx={{ 
                boxShadow: 'none', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: '8px',
                '&:before': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMoreIcon />} 
                sx={{ 
                  '& .MuiAccordionSummary-content': { 
                    m: 0,
                    my: 1.5
                  } 
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Box>

      {/* Footer Close button */}
      <Box sx={{ p: { xs: 2, md: 3 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <UIButton variant="outlined" size="large" onClick={onClose} sx={{ width: '100%' }}>
          Close
        </UIButton>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose} 
        TransitionComponent={SlideUpTransition} 
        fullScreen
        keepMounted
        sx={{ 
          '& .MuiDialog-paper': { 
            backgroundColor: 'background.default', 
            backgroundImage: 'none', 
            margin: 0,
            borderRadius: '16px 16px 0 0'
          } 
        }}
      >
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} keepMounted sx={{ '& .MuiDrawer-paper': { width: { md: '400px', lg: '480px' }, maxWidth: '40vw', backgroundColor: 'background.paper', border: 'none', boxShadow: theme.shadows[8], borderRadius: '16px 0 0 16px' } }}>
      {body}
    </Drawer>
  );
}


